<template>

  <stihl-modal id="org-modal" ref="orgModal" @modal-closed="$emit('modalClosed')">
    <the-overlay :active="state === 'INI'" />

    <div class="title-row" v-if="state === 'RDY'">
      <h3 class="title">
        <span class="d-inline-block">{{ orgDisplayName(organization) }}</span>
        <copy-pasta :data="organization.crmNo" icon-class="big-icon" />
        <stihl-icon
          icon="status-error"
          :width="20"
          :height="20"
          v-tooltip="$t('adm.label.inactiveOrg')"
          v-if="organization?.state === 'inactive'" />
      </h3>

      <title-buttons :org-id="organization.id" @add:clicked="showOrganizationModal = undefined" />
    </div>

    <div id="parent-org" v-if="state === 'RDY' && parent">
      <span class="subtitle" v-if="parent">{{ orgDisplayName(parent) }}</span>
    </div>

    <div class="modal-header" v-if="state === 'RDY'">
      <span
        class="subtitle-row"
        v-if="organization.createdOn"
        v-text="$t('adm.label.since', [formatDate(organization.createdOn)])" />
    </div>

    <stihl-flag :message="$t('adm.page.organization.flagLicenses')" v-if="state === 'RDY'" />
    <div class="row licenses" v-if="state === 'RDY'">
      <div class="col">
        <licenses-card :title="$t('adm.label.licenceCategory.tech')" :licenses="catTech" />
      </div>
      <div class="col">
        <licenses-card :title="$t('adm.label.licenceCategory.academy')" :licenses="catAcademy" />
      </div>
      <div class="col">
        <licenses-card :title="$t('adm.label.licenceCategory.b2b')" :licenses="catB2B" />
      </div>
    </div>

    <admins-panel
      id="admins"
      :primary-contact="organization.primaryContact"
      :admins="admins"
      @user-click="$emit('gotoUser', $event)"
      v-if="state === 'RDY'" />

    <div class="modal-footer" v-if="state === 'RDY'">
      <button type="button" class="btn link-with-icon" data-bs-dismiss="modal" :aria-label="$t('adm.button.cancel')">
        <img src="@/assets/img/icons/x.svg" :alt="$t('adm.button.cancel')">
        {{ $t('adm.button.cancel') }}
      </button>
    </div>

  </stihl-modal>

</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { localeDateFormat } from '@/components/utils';
import StihlModal from '@/components/ui/StihlModal.vue';
import Backend, {
  AdminDTO, LicenseData, OrganizationData, OrganizationDetails, orgDisplayName, QueryOptions,
} from '@/components/backend';
import StihlFlag from '@/components/ui/StihlFlag.vue';
import { i18n } from '@/lang';
import { Logger } from 'oidc-client-ts';
import { mapWritableState } from 'pinia';
import TheOverlay from '@/components/ui/TheOverlay.vue';
import CopyPasta from '@/components/ui/CopyPasta.vue';
import useAppStore from '@/components/store';
import StihlIcon from '@/components/ui/StihlIcon.vue';
import TitleButtons from './TitleButtons.vue';
import AdminsPanel from './AdminsPanel.vue';
import LicensesCard from './LicensesCard.vue';

export default defineComponent({
  name: 'OrganizationModal',
  components: {
    TitleButtons,
    StihlIcon,
    CopyPasta,
    TheOverlay,
    StihlFlag,
    LicensesCard,
    StihlModal,
    AdminsPanel,
  },
  emits: ['modalClosed', 'gotoUser'],

  props: {
    orgId: { type: String, required: true },
  },

  data: () => ({
    organization: {} as OrganizationDetails,
    parent: null as OrganizationData | null,
    licenses: [] as LicenseData[],
    admins: [] as AdminDTO[],
    state: 'INI' as 'INI' | 'RDY' | 'ERR',
  }),

  methods: {
    orgDisplayName,

    loadOrgDetails(id: string) {
      this.state = 'INI';
      Logger.info('org', 'loading organization by id:', id);

      Backend.validateToken().then((api) => api
        .loadOrganization(id, { $expand: 'licenses,admins,parent', $locale: i18n.global.locale } as QueryOptions)
        .then((r) => {
          this.organization = r.data;
          this.parent = r.extra?.parent?.find((o) => this.organization.parent === o.id) ?? null;
          this.licenses = r.extra?.licenses ?? [];
          this.admins = r.extra?.admins ?? [];
          // this.organization.createdTimestamp = '01.01.1981'
          this.state = 'RDY';
        })
        .catch((e) => {
          Logger.error('org', e);
          this.state = 'ERR';
        }));
    },

    category(cat: string): LicenseData[] {
      return this.licenses?.filter((x: LicenseData) => x.category === cat) ?? [];
    },

    formatDate(date: string) {
      return localeDateFormat(date, i18n.global.locale);
    },

    async copyCrmNo() {
      if (this.organization.crmNo) {
        navigator.clipboard.writeText(this.organization.crmNo).then(() => {
          this.$toast.success(this.$t('adm.page.organization.infoCopied'));
        });
      }
    },

  },

  computed: {
    // inject state getters
    ...mapWritableState(useAppStore, {
      showOrganizationModal: 'showOrganizationModal', // modal-control -> set org-id to show modal
    }),
    catTech(): LicenseData[] {
      return this.category('tech');
    },
    catAcademy(): LicenseData[] {
      return this.category('academy');
    },
    catB2B(): LicenseData[] {
      return this.category('business');
    },
  },

  beforeMount() {
    this.loadOrgDetails(this.orgId);
  },

});
</script>

<style>
#org-modal {
  --stihl-modal-content-gap: .75rem;
  --bs-modal-width: 95rem;
  --bs-modal-footer-border-width: 0;
}

#org-modal .title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 100%;
}

#org-modal .title {
  flex: 1 0 0;

  display: flex;
  align-content: center;
  gap: .5rem;

  color: black;
  font-feature-settings: 'clig' off, 'liga' off;

  /*font-family: Stihl-Contraface-Text;*/
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.5rem;

  margin: 0;
}

#org-modal .subtitle-row {
  font-size: 1rem;
  line-height: 1rem;
}

</style>
